import {
  UTModal,
  UTBox,
  UTTypography,
  UTButton,
  ColorVariables,
} from '@group-link-one/gl-ui/utilities';
import { useState, useEffect } from 'react';
import { getRoles, getUserRoles, updateUserRoles } from '../../UserManagement/Services';
import type { Role } from '../../UserManagement/Types/UserManagement.types';
import { useActionToast } from '@/Hooks/useActionToast';
import {
  formatRoleName,
  selectableRoles,
} from '@/Pages/Settings/UserManagement/Utils/userPresentation';

interface ManagePermissionsModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  userId: string;
}

export const ManagePermissionsModal = ({
  open,
  onClose,
  onSuccess,
  userId,
}: ManagePermissionsModalProps) => {
  const toast = useActionToast();
  const [roles, setRoles] = useState<Role[]>([]);
  const [selectedRoleIds, setSelectedRoleIds] = useState<string[]>([]);
  /*
    Roles this person holds that the picker does not offer — production has roles created straight
    in the database, named by no seed here. They are kept and sent back on save: a picker that does
    not show something must not silently take it away.
  */
  const [keptRoleIds, setKeptRoleIds] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      return;
    }

    const fetchData = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const [rolesData, userRolesData] = await Promise.all([getRoles(), getUserRoles(userId)]);

        const offered = selectableRoles(rolesData);
        setRoles(offered);

        // userRolesData.roles may be strings (role names) or objects — handle both
        const roleNames: string[] = userRolesData.roles.map((r: string | Role) =>
          typeof r === 'string' ? r : r.name,
        );
        const held = rolesData.filter(role => roleNames.includes(role.name));
        const offeredIds = new Set(offered.map(role => role.id));

        setSelectedRoleIds(held.filter(role => offeredIds.has(role.id)).map(role => role.id));
        setKeptRoleIds(held.filter(role => !offeredIds.has(role.id)).map(role => role.id));
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to fetch roles:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch roles');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [open, userId]);

  const handleRoleToggle = (roleId: string) => {
    setSelectedRoleIds(prev =>
      prev.includes(roleId) ? prev.filter(id => id !== roleId) : [...prev, roleId],
    );
  };

  const handleSave = async () => {
    try {
      setIsLoading(true);
      setError(null);
      await updateUserRoles(userId, [...selectedRoleIds, ...keptRoleIds]);
      toast.success('Permissões atualizadas');
      onSuccess?.();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to update roles:', err);
      setError(err instanceof Error ? err.message : 'Failed to update roles');
      toast.failure('Não foi possível atualizar as permissões', err);
      setIsLoading(false);
    }
  };

  return (
    <UTModal
      title='Alterar papel do usuário'
      open={open}
      onClose={onClose}
      hasButtonClose
      maxWidth='600px'
      content={
        <UTBox flexDirection='column' gap='20px' py='20px' maxHeight='500px' overflowY='auto'>
          {error && (
            <UTBox
              backgroundColor='rgba(220, 38, 38, 0.1)'
              borderRadius='8px'
              padding='12px'
              border='1px solid rgba(220, 38, 38, 0.3)'
            >
              <UTTypography fontSize='13px' fontWeight='400' color='#991b1b'>
                {error}
              </UTTypography>
            </UTBox>
          )}

          {isLoading ? (
            <UTTypography fontSize='14px' color='rgba(0,0,0,0.5)'>
              Carregando papéis...
            </UTTypography>
          ) : roles.length === 0 ? (
            <UTTypography fontSize='14px' color='rgba(0,0,0,0.5)'>
              Nenhum papel disponível
            </UTTypography>
          ) : (
            <UTBox flexDirection='column' gap='8px'>
              {roles.map(role => {
                const isSelected = selectedRoleIds.includes(role.id);
                const permissionCount = role.permissions?.length ?? 0;

                return (
                  <UTBox
                    key={role.id}
                    p='14px 16px'
                    borderRadius='8px'
                    border={`2px solid ${
                      isSelected ? ColorVariables.ACCENT.ACCENT_COLOR_PRIMARY_BACKGROUND : '#e0e0e0'
                    }`}
                    backgroundColor={isSelected ? 'rgba(74, 144, 226, 0.06)' : '#fff'}
                    cursor='pointer'
                    onClick={() => handleRoleToggle(role.id)}
                  >
                    <UTBox flexDirection='row' alignItems='flex-start' gap='12px' width='100%'>
                      <input
                        type='checkbox'
                        checked={isSelected}
                        onChange={() => handleRoleToggle(role.id)}
                        style={{ marginTop: '3px', marginRight: '4px' }}
                      />
                      <UTBox flexDirection='column' gap='4px' flex={1}>
                        <UTBox flexDirection='row' alignItems='center' gap='8px'>
                          <UTTypography fontSize='14px' fontWeight='600'>
                            {formatRoleName(role.name)}
                          </UTTypography>
                          <UTBox p='2px 8px' borderRadius='12px' backgroundColor='rgba(0,0,0,0.06)'>
                            <UTTypography fontSize='11px' color='rgba(0,0,0,0.5)'>
                              {permissionCount} {permissionCount === 1 ? 'permissão' : 'permissões'}
                            </UTTypography>
                          </UTBox>
                        </UTBox>
                        {role.description && (
                          <UTTypography fontSize='12px' color='rgba(0,0,0,0.55)'>
                            {role.description}
                          </UTTypography>
                        )}
                        {isSelected && role.permissions && role.permissions.length > 0 && (
                          <UTBox flexDirection='row' flexWrap='wrap' gap='4px' mt='8px'>
                            {role.permissions.map(perm => (
                              <UTBox
                                key={perm.id}
                                p='2px 8px'
                                borderRadius='4px'
                                backgroundColor='rgba(74, 144, 226, 0.1)'
                              >
                                <UTTypography fontSize='11px' color='rgba(0,0,0,0.6)'>
                                  {perm.name}
                                </UTTypography>
                              </UTBox>
                            ))}
                          </UTBox>
                        )}
                      </UTBox>
                    </UTBox>
                  </UTBox>
                );
              })}
            </UTBox>
          )}

          <UTBox
            flexDirection='row'
            gap='12px'
            justifyContent='flex-end'
            mt='20px'
            borderTop='1px solid rgba(0,0,0,0.1)'
            pt='20px'
          >
            <UTButton variant='secondary' text='Cancelar' onClick={onClose} disabled={isLoading} />
            <UTButton
              variant='primary'
              text={isLoading ? 'Salvando...' : 'Salvar papéis'}
              onClick={handleSave}
              disabled={isLoading}
            />
          </UTBox>
        </UTBox>
      }
    />
  );
};
